import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Zap, Gift, ShoppingBag, Heart, Truck, CreditCard } from 'lucide-react';

export default function RatgeberLastMinuteGeschenke() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/ratgeber"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4">
            <Calendar className="w-4 h-4" />
            <span>Aktualisiert: Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">
            Last Minute Eid Geschenke: Schnelle Ideen wenn die Zeit knapp wird
          </h1>
          <p className="text-lg text-white/80 leading-relaxed">
            Keine Panik! Diese Geschenkideen können Sie auch kurzfristig noch besorgen – 
            für ein gelungenes Eid-Fest trotz Zeitdruck.
          </p>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          
          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-[#2C332E] leading-relaxed mb-8">
              Eid steht vor der Tür und Sie haben noch keine Geschenke? Das passiert den Besten von uns! 
              Der Fastenmonat ist intensiv, die Zeit verfliegt, und plötzlich ist Eid nur noch wenige 
              Tage entfernt. Aber keine Sorge – mit diesem Guide finden Sie auch in letzter Minute 
              das perfekte Geschenk für Ihre Liebsten.
            </p>

            {/* Urgent Banner */}
            <div className="bg-[#D4C5A9]/20 border-2 border-[#D4C5A9] rounded-2xl p-6 mb-12">
              <div className="flex items-center gap-3 mb-3">
                <Zap className="w-6 h-6 text-[#4A5D4F]" />
                <h3 className="font-serif text-xl font-medium text-[#2C332E]">Schnell-Übersicht</h3>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#4A5D4F]" />
                  <span><strong>1-2 Tage:</strong> Online + Express</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 text-[#4A5D4F]" />
                  <span><strong>Heute:</strong> Lokale Geschäfte</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-[#4A5D4F]" />
                  <span><strong>Sofort:</strong> Digitale Geschenke</span>
                </div>
              </div>
            </div>

            {/* Table of Contents */}
            <div className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 mb-12">
              <h2 className="font-serif text-xl font-medium text-[#2C332E] mb-4">
                ⏰ Inhaltsverzeichnis
              </h2>
              <ul className="space-y-2 text-[#4A5D4F]">
                <li><a href="#sofort" className="hover:underline">1. Sofort verfügbar: Digitale Geschenke</a></li>
                <li><a href="#heute" className="hover:underline">2. Heute noch kaufen: Lokale Optionen</a></li>
                <li><a href="#express" className="hover:underline">3. Express-Lieferung: Online-Shopping</a></li>
                <li><a href="#selbstgemacht" className="hover:underline">4. Schnell selbstgemacht: DIY in unter 1 Stunde</a></li>
                <li><a href="#erlebnisse" className="hover:underline">5. Erlebnisgeschenke: Zeit statt Dinge</a></li>
                <li><a href="#geld" className="hover:underline">6. Geldgeschenke: Eidi richtig verpacken</a></li>
                <li><a href="#checkliste" className="hover:underline">7. Last-Minute-Checkliste</a></li>
              </ul>
            </div>

            {/* Section 1 - Sofort */}
            <section id="sofort" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  1. Sofort verfügbar: Digitale Geschenke
                </h2>
              </div>
              
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Digitale Geschenke sind die schnellste Lösung – Sie können sie innerhalb von Minuten 
                kaufen und per E-Mail oder WhatsApp versenden. Perfekt, wenn Eid bereits morgen ist!
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Gutscheine & Gift Cards</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Amazon Gutschein:</strong> Universell einsetzbar, sofort per E-Mail</li>
                <li><strong>Zalando/About You:</strong> Für modebewusste Beschenkte</li>
                <li><strong>Douglas/Sephora:</strong> Beauty und Parfüm</li>
                <li><strong>IKEA:</strong> Für das neue Zuhause</li>
                <li><strong>Restaurant-Gutscheine:</strong> Für ein gemeinsames Halal-Essen</li>
                <li><strong>Streaming-Abos:</strong> Netflix, Spotify, Audible</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Digitale Spenden</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Eine Spende im Namen des Beschenkten ist ein bedeutungsvolles Geschenk – besonders zu Eid:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Qurbani-Spende:</strong> Ein Opfertier für Bedürftige (ideal zu Eid ul Adha)</li>
                <li><strong>Brunnen-Projekt:</strong> Nachhaltiges Geschenk mit Sadaqah Jariyah</li>
                <li><strong>Waisenkind-Patenschaft:</strong> Monatlich oder einmalig</li>
                <li><strong>Lebensmittelspende:</strong> Für Familien in Not</li>
              </ul>

              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">💡 Tipp: Spende schön verpacken</h4>
                <p className="text-[#4B5563]">
                  Erstellen Sie eine hübsche Karte (digital oder handgeschrieben) mit dem Text: 
                  "In deinem Namen wurde [Spende] gespendet. Möge Allah es annehmen und vervielfachen. 
                  Eid Mubarak!" – Das macht das Geschenk persönlich und bedeutungsvoll.
                </p>
              </div>
            </section>

            {/* Section 2 - Heute */}
            <section id="heute" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  2. Heute noch kaufen: Lokale Optionen
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Sie haben noch einen Tag Zeit? Diese Geschäfte haben fast immer passende Geschenke vorrätig:
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Drogeriemärkte (dm, Rossmann, Müller)</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li>Parfüm-Sets und Düfte</li>
                <li>Pflegeprodukte und Geschenksets</li>
                <li>Kerzen und Raumdüfte</li>
                <li>Schokolade und Süßigkeiten</li>
                <li>Geschenkverpackung und Karten</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Supermärkte (Rewe, Edeka, Kaufland)</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li>Hochwertige Datteln (oft in der internationalen Abteilung)</li>
                <li>Pralinen und Schokoladen-Boxen</li>
                <li>Tee- und Kaffee-Spezialitäten</li>
                <li>Blumen und Pflanzen</li>
                <li>Geschenkkörbe zusammenstellen</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Orientalische/Türkische Supermärkte</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li>Premium-Datteln verschiedener Sorten</li>
                <li>Baklava und orientalische Süßigkeiten</li>
                <li>Türkischer Tee und Mokka</li>
                <li>Lokum (Turkish Delight)</li>
                <li>Halal-Süßigkeiten</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Weitere Optionen vor Ort</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Buchhandlungen:</strong> Bücher, Journals, Kalender</li>
                <li><strong>Parfümerien:</strong> Douglas, Sephora, Marionnaud</li>
                <li><strong>Schmuckgeschäfte:</strong> Ketten, Armbänder, Ohrringe</li>
                <li><strong>Spielzeugläden:</strong> Für Kinder</li>
                <li><strong>Florist:</strong> Blumenstrauß oder Pflanze</li>
              </ul>
            </section>

            {/* Section 3 - Express */}
            <section id="express" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Truck className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  3. Express-Lieferung: Online-Shopping
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Haben Sie noch 1-2 Tage Zeit? Mit Express-Versand bekommen Sie viele Produkte rechtzeitig:
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Amazon Prime (Lieferung in 1-2 Tagen)</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Parfüm-Geschenksets:</strong> Swiss Arabian, RITUALS, etc.</li>
                <li><strong>Islamische Bücher:</strong> Kinderbücher, Quran-Ausgaben</li>
                <li><strong>Elektronik:</strong> Kopfhörer, Gadgets</li>
                <li><strong>Spielzeug für Kinder:</strong> Riesige Auswahl</li>
                <li><strong>Schmuck:</strong> Mit Prime-Versand</li>
              </ul>

              <div className="bg-[#D4C5A9]/20 rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">⚡ Pro-Tipp: Amazon Same-Day</h4>
                <p className="text-[#4B5563]">
                  In vielen deutschen Städten bietet Amazon Same-Day-Lieferung an. Bestellen Sie 
                  vormittags und erhalten Sie Ihr Geschenk noch am selben Tag. Achten Sie auf das 
                  "Heute bestellen, heute erhalten"-Label.
                </p>
              </div>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Weitere Online-Shops mit schneller Lieferung</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Zalando:</strong> Express-Lieferung für Mode und Accessoires</li>
                <li><strong>Flaconi/Douglas Online:</strong> Parfüm und Beauty</li>
                <li><strong>AboutYou:</strong> Mode mit Expressversand</li>
                <li><strong>MediaMarkt/Saturn:</strong> Elektronik mit Expresslieferung</li>
              </ul>
            </section>

            {/* Section 4 - Selbstgemacht */}
            <section id="selbstgemacht" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  4. Schnell selbstgemacht: DIY in unter 1 Stunde
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Selbstgemachte Geschenke zeigen besondere Wertschätzung – und diese Ideen sind 
                schnell umgesetzt:
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Kulinarische Schnell-Geschenke</h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">🍫 Dattel-Trüffel (20 Min.)</h4>
                  <p className="text-sm text-[#4B5563]">
                    Entkernte Datteln mit Nüssen füllen, in geschmolzene Schokolade tauchen, 
                    mit Kokosraspeln bestreuen. Fertig!
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">🫖 Gewürz-Tee-Mischung (15 Min.)</h4>
                  <p className="text-sm text-[#4B5563]">
                    Schwarztee mit Kardamom, Zimt, Nelken und Ingwer mischen. In ein 
                    hübsches Glas füllen, Etikett drauf.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">🍪 Energy Balls (30 Min.)</h4>
                  <p className="text-sm text-[#4B5563]">
                    Datteln, Nüsse, Kakao im Mixer zerkleinern, zu Kugeln formen. 
                    Gesund und lecker!
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">🧈 Aromatisiertes Öl (10 Min.)</h4>
                  <p className="text-sm text-[#4B5563]">
                    Olivenöl mit Kräutern, Knoblauch oder Chili in eine Flasche füllen. 
                    Sofort verschenkbar!
                  </p>
                </div>
              </div>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Kreative Schnell-Geschenke</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Persönlicher Brief:</strong> Schreiben Sie, warum diese Person Ihnen wichtig ist</li>
                <li><strong>Foto-Collage:</strong> Gemeinsame Erinnerungen ausdrucken und rahmen</li>
                <li><strong>Gutschein-Buch:</strong> "Gutschein für: 1x Kochen, 1x Babysitting, etc."</li>
                <li><strong>Playlist:</strong> Spotify-Playlist mit bedeutungsvollen Liedern erstellen</li>
              </ul>
            </section>

            {/* Section 5 - Erlebnisse */}
            <section id="erlebnisse" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  5. Erlebnisgeschenke: Zeit statt Dinge
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Manchmal ist gemeinsame Zeit das wertvollste Geschenk. Diese Ideen können Sie 
                sofort "verschenken" – ohne etwas kaufen zu müssen:
              </p>

              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Gemeinsames Eid-Frühstück:</strong> Laden Sie zum Brunch ein</li>
                <li><strong>Ausflug planen:</strong> Zoo, Museum, Park – Gutschein gestalten</li>
                <li><strong>Kochabend:</strong> Zusammen ein neues Rezept ausprobieren</li>
                <li><strong>Spa-Tag zu Hause:</strong> Mit Gesichtsmasken und Tee</li>
                <li><strong>Film-Marathon:</strong> Mit Popcorn und Snacks</li>
                <li><strong>Foto-Shooting:</strong> Familien- oder Freundefoto als Erinnerung</li>
              </ul>

              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🎁 So verpacken Sie ein Erlebnisgeschenk</h4>
                <p className="text-[#4B5563]">
                  Gestalten Sie eine schöne Karte oder einen selbstgemachten Gutschein. Schreiben Sie: 
                  "Eid Mubarak! Mein Geschenk an dich: [Erlebnis]. Termin nach Absprache. 
                  Ich freue mich auf unsere gemeinsame Zeit!" – Das zeigt Wertschätzung und Vorfreude.
                </p>
              </div>
            </section>

            {/* Section 6 - Geld */}
            <section id="geld" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  6. Geldgeschenke: Eidi richtig verpacken
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Geldgeschenke (Eidi) sind zu Eid absolut akzeptabel und weit verbreitet – 
                besonders von Älteren an Jüngere. Mit der richtigen Präsentation wird es zum 
                besonderen Geschenk:
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Kreative Verpackungsideen</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Eid Mubarak-Umschläge:</strong> Spezielle Umschläge mit islamischen Motiven</li>
                <li><strong>Origami-Faltung:</strong> Geldscheine zu Sternen oder Herzen falten</li>
                <li><strong>In einem Buch verstecken:</strong> "Das Geheimnis liegt auf Seite 50..."</li>
                <li><strong>Ballon-Überraschung:</strong> Geld in einen Ballon stecken und aufblasen</li>
                <li><strong>Schatzkiste:</strong> Kleine Box mit Deko und Geldschein</li>
                <li><strong>Mit Süßigkeiten kombinieren:</strong> Geld zwischen Pralinen verstecken</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Richtwerte für Eidi</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Es gibt keine festen Regeln, aber hier sind übliche Beträge in Deutschland:
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">Für Kinder</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Neffen/Nichten: 10-50€</li>
                    <li>• Kinder von Freunden: 5-20€</li>
                    <li>• Nachbarskinder: 5-10€</li>
                  </ul>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">Für Erwachsene</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Geschwister: 20-100€</li>
                    <li>• Eltern: 50-200€+</li>
                    <li>• Enge Freunde: 20-50€</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 7 - Checkliste */}
            <section id="checkliste" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  7. Last-Minute-Checkliste
                </h2>
              </div>

              <div className="bg-[#4A5D4F] text-white rounded-xl p-6 mb-6">
                <h3 className="font-serif text-xl font-medium mb-4">✅ Ihre Eid-Geschenke-Checkliste</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded border border-white/50 flex-shrink-0 mt-0.5" />
                    <span>Liste aller Personen erstellen, die ein Geschenk bekommen sollen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded border border-white/50 flex-shrink-0 mt-0.5" />
                    <span>Budget pro Person festlegen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded border border-white/50 flex-shrink-0 mt-0.5" />
                    <span>Geschenkidee für jede Person notieren</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded border border-white/50 flex-shrink-0 mt-0.5" />
                    <span>Geschenkverpackung und Karten besorgen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded border border-white/50 flex-shrink-0 mt-0.5" />
                    <span>Geschenke einpacken und beschriften</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded border border-white/50 flex-shrink-0 mt-0.5" />
                    <span>Bargeld für Eidi (Kinder) vorbereiten</span>
                  </li>
                </ul>
              </div>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Für nächstes Jahr merken</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Um Last-Minute-Stress zu vermeiden, planen Sie für nächstes Jahr:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li>Geschenke während des Jahres kaufen, wenn Sie etwas Passendes sehen</li>
                <li>Eine Geschenke-Liste führen mit Ideen für verschiedene Personen</li>
                <li>Spätestens 2 Wochen vor Eid mit dem Einkaufen beginnen</li>
                <li>Verpackungsmaterial vorrätig haben</li>
              </ul>
            </section>

            {/* Conclusion */}
            <section className="mb-12">
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-6">
                Fazit: Es ist nie zu spät für ein gutes Geschenk
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Auch wenn die Zeit knapp ist, können Sie mit diesen Ideen noch ein wundervolles 
                Eid-Geschenk finden. Denken Sie daran: Es geht nicht um den Preis oder die Perfektion, 
                sondern um die Geste und die Liebe dahinter.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Ein selbstgeschriebener Brief kann mehr bedeuten als ein teures Parfüm. Eine Spende 
                im Namen des Beschenkten kann mehr berühren als ein gekauftes Geschenk. Und manchmal 
                ist gemeinsame Zeit das Wertvollste, was wir schenken können.
              </p>
              <p className="text-xl text-[#4A5D4F] font-medium">
                Eid Mubarak – möge Ihr Fest gesegnet sein! 🌙
              </p>
            </section>

            {/* CTA */}
            <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
              <h3 className="font-serif text-2xl font-medium text-white mb-4">
                Noch mehr Geschenkideen entdecken
              </h3>
              <p className="text-white/80 mb-6">
                Stöbern Sie durch unsere handverlesene Auswahl an Eid Geschenken
              </p>
              <Link
                to="/geschenke"
                className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors"
              >
                Jetzt entdecken
                <Gift className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
